import {
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  formatDateParts,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import ButtonWidget from './widgets/button-widget';
import FooterWidget from './widgets/footer-widget';
import { templateStaticDefaults } from './template-defaults';
import { BlockRenderer } from './widgets/block-renderer';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';
import { BLOCK_ID } from './widgets/block-ids';

const DEFAULTS = templateStaticDefaults.capri;

const CAPRI_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.salutation]: {
    fitContent: true,
    color: 'var(--button-text)',
    fontFamily: 'var(--body-font)',
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '24px',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    background: 'var(--accent)',
    borderRadius: '100px',
    padding: '4px 12px',
    margin: '32px 56px 0 56px',
  },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '72px',
    fontWeight: 400,
    lineHeight: '52px',
    letterSpacing: '-1.5px',
    whiteSpace: 'pre-line',
    padding: '32px 56px',
  },
  [BLOCK_ID.mainImage]: { width: 600, height: 400, borderRadius: 'var(--image-radius)' },
  [BLOCK_ID.description]: {
    color: 'var(--body)',
    fontFamily: 'var(--body-font)',
    fontSize: '16px',
    lineHeight: '24px',
    padding: '32px 56px',
  },
  [BLOCK_ID.dateTimeCard]: { padding: '12px 56px 0 56px' },
  [BLOCK_ID.cta]: {
    size: 'large',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--body-font)',
    fontSize: '16px',
    fontWeight: 700,
    padding: '32px 56px 20px 56px',
  },
  [BLOCK_ID.legalText]: {
    color: '#8F8F8F',
    fontSize: '11px',
    fontWeight: 400,
    fontFamily: 'var(--body-font)',
    lineHeight: '18px',
    letterSpacing: 'normal',
    textAlign: 'center',
    padding: '0 56px 40px 56px',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

export const Capri = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  salutation,
  setSalutation,
  title,
  setTitle,
  description,
  setDescription,
  cta,
  setCta,
  setMainImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const secondaryAccent = resolveThemedColor(
    customizations?.secondaryAccentColor,
    themeColors?.secondaryAccentColor,
    DEFAULTS.secondaryAccentColor
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '100px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '0px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const anchorHref = href || '#';
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Instrument Serif", Georgia, serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    '"Instrument Sans", Arial, sans-serif'
  );
  const _salutation = salutation ?? (customizations?.salutation || DEFAULTS.salutation);
  const eventTitle = title ?? (customizations?.title || DEFAULTS.title);
  const eventDescription = description || customizations?.description || DEFAULTS.description;
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = '#8F8F8F';
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      secondaryAccent,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: CAPRI_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Capri"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--secondary-accent': secondaryAccent,
            '--body': body,
            '--heading': heading,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px', background: 'var(--container)' }}
              >
                <tbody>
                  <tr>
                    <td>
                      {blocks.map((block) => (
                        <BlockShell
                          key={block.id}
                          block={block}
                          isEditMode={!!isEditMode}
                          isFirst={block.id === blocks[0]?.id}
                          isLast={block.id === blocks[blocks.length - 1]?.id}
                          selected={block.id === selectedBlockId}
                          onSelect={onSelectBlock}
                          onDelete={onDeleteBlock}
                          onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                          onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                        >
                          <BlockRenderer
                            block={block}
                            stylePreset={CAPRI_BLOCK_PRESETS[block.id]}
                            onValueChange={onUpdateBlockValue}
                            onImageChange={onUpdateBlockImage}
                            context={blockContext}
                          />
                        </BlockShell>
                      ))}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td>
                        <CalendarLinksWidget links={calendarLinks} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Capri"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--secondary-accent': secondaryAccent,
          '--body': body,
          '--heading': heading,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px', background: 'var(--container)' }}
            >
              <tbody>
                <tr>
                  <td>
                    <HeaderWidget
                      value={_salutation}
                      setValue={setSalutation}
                      fitContent
                      color="var(--button-text)"
                      fontFamily="var(--body-font)"
                      fontSize="16px"
                      fontWeight={700}
                      lineHeight="24px"
                      letterSpacing="0.4px"
                      textTransform="uppercase"
                      background="var(--accent)"
                      borderRadius="100px"
                      padding="4px 12px"
                      margin="32px 56px 0 56px"
                    />

                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="72px"
                      fontWeight={400}
                      lineHeight="52px"
                      letterSpacing="-1.5px"
                      whiteSpace="pre-line"
                      padding="32px 56px"
                    />

                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      width={600}
                      height={400}
                      borderRadius={imageRadius}
                    />

                    <ParagraphWidget
                      value={eventDescription}
                      setValue={setDescription}
                      color="var(--body)"
                      fontFamily="var(--body-font)"
                      fontSize="16px"
                      lineHeight="24px"
                      padding="32px 56px"
                    />

                    {sessionTimestamp && (
                      <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
                        <tbody>
                          <tr>
                            <td style={{ padding: '12px 56px 0 56px' }}>
                              <table
                                width="100%"
                                cellPadding={0}
                                cellSpacing={0}
                                border={0}
                                role="presentation"
                                style={{ border: '1px solid var(--accent)', borderRadius: '12px' }}
                              >
                                <tbody>
                                  <tr>
                                    <td
                                      style={{
                                        padding: '20px',
                                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                                      }}
                                    >
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--accent)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 600,
                                              }}
                                            >
                                              Date
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--heading)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 400,
                                              }}
                                            >
                                              {sessionDateLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td style={{ padding: '20px' }}>
                                      <table
                                        width="100%"
                                        cellPadding={0}
                                        cellSpacing={0}
                                        border={0}
                                        role="presentation"
                                      >
                                        <tbody>
                                          <tr>
                                            <td
                                              style={{
                                                color: 'var(--accent)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 600,
                                              }}
                                            >
                                              Time
                                            </td>
                                            <td
                                              align="right"
                                              style={{
                                                color: 'var(--heading)',
                                                fontSize: '14px',
                                                fontFamily: 'var(--body-font)',
                                                fontWeight: 400,
                                              }}
                                            >
                                              {sessionTimeLabel}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}

                    <ButtonWidget
                      value={_cta}
                      setValue={setCta}
                      href={anchorHref}
                      size="large"
                      color="var(--button-text)"
                      buttonColor="var(--button-color)"
                      fontFamily="var(--body-font)"
                      fontSize="16px"
                      fontWeight={700}
                      borderRadius={buttonRadius}
                      padding="32px 56px 20px 56px"
                    />

                    <ParagraphWidget
                      value={DEFAULTS.complianceText}
                      color="#8F8F8F"
                      fontSize="11px"
                      fontFamily="var(--body-font)"
                      lineHeight="18px"
                      textAlign="center"
                      padding="0 56px 40px 56px"
                    />
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--secondary-accent)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--secondary-accent)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--secondary-accent)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--secondary-accent)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--secondary-accent)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
